import React from "react";
import { Helmet } from "react-helmet-async";
import ProductSchema from "../components/ProductSchema";
import { Link, useNavigate } from "react-router-dom";
import CosmicBackground from "../components/CosmicBackground";
// Reuse the styles from the (already live, hydration-safe) /ai-astrologer page so
// every SEO landing page looks consistent.
import styles from "./AiAstrologerPageLegacy.module.css";

export type LandingSection = {
  h2: string;
  body?: string[]; // paragraphs
  bullets?: string[]; // <ul>
  steps?: string[]; // <ol>
};

export type LandingFaq = { q: string; a: string };

export type LandingConfig = {
  url: string; // canonical, e.g. https://astroaura.ai/free-kundli
  title: string;
  description: string;
  eyebrow: string;
  h1Pre: string;
  h1Accent: string;
  h1Post?: string;
  sub: string;
  primaryCta: string;
  secondaryCta?: string;
  sections: LandingSection[];
  faqs: LandingFaq[];
  schemaName: string;
  schemaDescription: string;
  finalHeading: string;
  finalText: string;
  /** ISO country codes this page's service covers. Defaults to the full
      footprint. Narrow it only for a page that genuinely is single-market. */
  areaServed?: string[];
  /** Currency for the free-tier Offer. Cosmetic on a zero-price offer, but a
      page written for the Gulf should not quote INR. */
  priceCurrency?: string;
  /** Extra internal links in the footer strip, for geo pages that should feed
      the hub pages and be fed by them. */
  relatedLinks?: { to: string; label: string }[];
  /** YMYL disclaimer, rendered under the final CTA. Set it on pages that touch
      marriage, health, money or fear (dosha pages). Pages without one render
      exactly as before. */
  disclaimer?: string;
};

// Every market the product actually serves. This used to be the string "IN" on
// all four landing pages, which told Google the service was India-only, the
// opposite of what a globally available app wants, and a plausible part of why
// nothing surfaced on google.ae or google.com.
const DEFAULT_AREA_SERVED = ["IN", "AE", "US", "GB", "CA", "AU", "SG"];

export default function SeoLandingPageLegacy({
  config,
  tool,
}: {
  config: LandingConfig;
  /** Optional interactive widget (a calculator) rendered directly under the
      hero, above the prose. Pages without one are unchanged. */
  tool?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const goToChat = () => navigate("/chat");

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: config.schemaName,
    serviceType: "AI Vedic astrology",
    url: config.url,
    areaServed: (config.areaServed ?? DEFAULT_AREA_SERVED).map((c) => ({
      "@type": "Country",
      identifier: c,
    })),
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: "https://astroaura.ai/chat",
      availableLanguage: ["en", "hi"],
    },
    provider: {
      "@type": "Organization",
      name: "Aura AI",
      url: "https://astroaura.ai/",
    },
    description: config.schemaDescription,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: config.priceCurrency ?? "INR",
    },
  };

  // HowTo, emitted when the page has a genuine step list. Answer engines lift
  // step lists verbatim, so this is one of the cheapest ways onto an AI answer.
  const stepSection = config.sections.find((s) => s.steps && s.steps.length);
  const howToJsonLd = stepSection
    ? {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: stepSection.h2,
        description: config.schemaDescription,
        totalTime: "PT2M",
        step: stepSection.steps!.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.split(/[.:]/)[0].slice(0, 70),
          text: s,
        })),
      }
    : null;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: config.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://astroaura.ai/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: `${config.h1Pre} ${config.h1Accent}${config.h1Post ?? ""}`.trim(),
        item: config.url,
      },
    ],
  };

  return (
    <div className={styles.page}>
      <ProductSchema />
      <Helmet>
        <title>{config.title}</title>
        <meta name="description" content={config.description} />
        <link rel="canonical" href={config.url} />
        {/* hreflang is NOT set here, IndexPolicy emits the en + x-default pair
            for every indexable route in one place. Setting it here too gave
            these pages duplicate alternate links. */}
        <meta property="og:locale" content="en" />
        <meta property="og:title" content={config.title} />
        <meta property="og:description" content={config.description} />
        <meta property="og:url" content={config.url} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://astroaura.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.title} />
        <meta name="twitter:description" content={config.description} />
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
        {howToJsonLd ? (
          <script type="application/ld+json">
            {JSON.stringify(howToJsonLd)}
          </script>
        ) : null}
      </Helmet>

      <CosmicBackground />

      <main className={styles.wrap}>
        <header className={styles.hero}>
          <p className={styles.eyebrow}>{config.eyebrow}</p>
          <h1 className={styles.h1}>
            {config.h1Pre} <span className={styles.accent}>{config.h1Accent}</span>
            {config.h1Post ? config.h1Post : null}
          </h1>
          <p className={styles.sub}>{config.sub}</p>
          <div className={styles.ctaRow}>
            {/* When the page carries a calculator, the hero CTA is a real
                anchor to it rather than a JS scroll. A previous attempt used
                scrollIntoView/window.scrollTo from an onClick and did nothing
                at all - App.tsx runs `window.scrollTo(0,0)` on every pathname
                change, and a hash link sidesteps that entirely because the
                browser owns the scroll and the pathname never changes.
                Pages with no tool keep the old behaviour. */}
            {tool ? (
              <a className={styles.ctaPrimary} href="#tool">
                {config.primaryCta}
              </a>
            ) : (
              <button className={styles.ctaPrimary} onClick={goToChat}>
                {config.primaryCta}
              </button>
            )}
            {config.secondaryCta ? (
              <button className={styles.ctaGhost} onClick={goToChat}>
                {config.secondaryCta}
              </button>
            ) : null}
          </div>
        </header>

        {tool ? (
          <div className={styles.toolSlot} id="tool">
            {tool}
          </div>
        ) : null}

        {config.sections.map((s) => (
          <section className={styles.section} key={s.h2}>
            <h2 className={styles.h2}>{s.h2}</h2>
            {s.body
              ? s.body.map((p, i) => <p key={i}>{p}</p>)
              : null}
            {s.steps ? (
              <ol className={styles.steps}>
                {s.steps.map((li, i) => (
                  <li key={i}>{li}</li>
                ))}
              </ol>
            ) : null}
            {s.bullets ? (
              <ul className={styles.asks}>
                {s.bullets.map((li, i) => (
                  <li key={i}>{li}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}

        <section className={styles.section}>
          <h2 className={styles.h2}>Frequently asked questions</h2>
          <div className={styles.faq}>
            {config.faqs.map((f) => (
              <details key={f.q} className={styles.faqItem}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={styles.finalCta}>
          <h2 className={styles.h2}>{config.finalHeading}</h2>
          <p>{config.finalText}</p>
          <button className={styles.ctaPrimary} onClick={goToChat}>
            Start free
          </button>
          <p className={styles.links}>
            Explore <Link to="/ai-astrologer">AI astrologer</Link>,{" "}
            <Link to="/free-kundli">free kundli</Link>,{" "}
            <Link to="/kundli-matching">kundli matching</Link>,{" "}
            <Link to="/daily-horoscope">daily horoscope</Link>
            {(config.relatedLinks ?? []).map((l) => (
              <span key={l.to}>
                , <Link to={l.to}>{l.label}</Link>
              </span>
            ))}
            , or go <Link to="/">home</Link>.
          </p>
        </section>

        {config.disclaimer && (
          <section className={styles.finalCta}>
            <p className={styles.links}>{config.disclaimer}</p>
          </section>
        )}
      </main>
    </div>
  );
}
